/**
 * useForecastingService — React hook for forecasting pipeline API.
 *
 * Provides loading/error state management and polling for background jobs.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import * as api from '../services/forecastingService';

export function useForecastingService() {
    // Shared state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Data pipeline state
    const [comstockStats, setComstockStats] = useState(null);
    const [samplingCoverage, setSamplingCoverage] = useState(null);
    const [downloadJobs, setDownloadJobs] = useState(null);
    const [samplingJobs, setSamplingJobs] = useState(null);

    // Model state
    const [models, setModels] = useState(null);
    const [trainingJob, setTrainingJob] = useState(null);

    // Validation state
    const [validationRuns, setValidationRuns] = useState(null);
    const [activeValidation, setActiveValidation] = useState(null);
    const [validationResults, setValidationResults] = useState(null);

    // Forecast state
    const [forecastResult, setForecastResult] = useState(null);

    // Polling refs
    const pollRef = useRef(null);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    // ---- Data Pipeline ----

    const refreshComstockStatus = useCallback(async () => {
        try {
            const [stats, dlStatus, smpStatus, coverage] = await Promise.all([
                api.getComstockStats(),
                api.getComstockStatus(),
                api.getSamplingStatus(),
                api.getSamplingCoverage(),
            ]);
            setComstockStats(stats);
            setDownloadJobs(dlStatus);
            setSamplingJobs(smpStatus);
            setSamplingCoverage(coverage);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    const startDownload = useCallback(async (buildingType) => {
        setError(null);
        setLoading(true);
        try {
            const result = await api.startComstockDownload(buildingType);
            setDownloadJobs(prev => ({ ...prev, ...result }));
            return result;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const startSampling = useCallback(async (config) => {
        setError(null);
        setLoading(true);
        try {
            const result = await api.buildSample(config);
            setSamplingJobs(prev => ({ ...prev, ...result }));
            return result;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // ---- Models ----

    const refreshModels = useCallback(async () => {
        try {
            const result = await api.listModels();
            setModels(result);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    const startTraining = useCallback(async (config) => {
        setError(null);
        setLoading(true);
        try {
            const result = await api.startTraining(config);
            setTrainingJob(result);

            // Start polling for training status
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                try {
                    const status = await api.getTrainingStatus(result.job_id);
                    setTrainingJob(status);
                    if (status.status === 'completed' || status.status === 'failed') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        refreshModels();
                    }
                } catch (e) {
                    // Polling failure is non-fatal
                }
            }, 3000);

            return result;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [refreshModels]);

    // ---- Validation ----

    const refreshValidationRuns = useCallback(async () => {
        try {
            const result = await api.listValidationRuns();
            setValidationRuns(result.runs || []);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    const startValidation = useCallback(async (config) => {
        setError(null);
        setLoading(true);
        try {
            const result = await api.startValidationRun(config);
            setActiveValidation(result);

            // Poll for completion
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                try {
                    const status = await api.getValidationRun(result.run_id);
                    setActiveValidation(status);
                    if (status.status === 'completed' || status.status === 'failed') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        // Fetch full results
                        const full = await api.getValidationRun(result.run_id, true);
                        setValidationResults(full.results || full);
                        refreshValidationRuns();
                    }
                } catch (e) {
                    // Polling failure is non-fatal
                }
            }, 5000);

            return result;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [refreshValidationRuns]);

    const startColdStartTest = useCallback(async (config) => {
        setError(null);
        setLoading(true);
        try {
            const result = await api.startColdStartTest(config);
            setActiveValidation(result);

            // Poll for completion
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                try {
                    const status = await api.getValidationRun(result.run_id);
                    setActiveValidation(status);
                    if (status.status === 'completed' || status.status === 'failed') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        const full = await api.getValidationRun(result.run_id, true);
                        setValidationResults(full.results || full);
                        refreshValidationRuns();
                    }
                } catch (e) {
                    // Polling failure is non-fatal
                }
            }, 5000);

            return result;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [refreshValidationRuns]);

    const loadValidationResults = useCallback(async (runId) => {
        setError(null);
        try {
            const full = await api.getValidationRun(runId, true);
            setValidationResults(full.results || full);
            setActiveValidation(full);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    // ---- Forecast ----

    const generateForecast = useCallback(async (buildingMetadata, weather8760) => {
        setError(null);
        setLoading(true);
        try {
            const result = await api.runPrediction(buildingMetadata, weather8760);
            setForecastResult(result);
            return result;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        // State
        loading,
        error,
        comstockStats,
        samplingCoverage,
        downloadJobs,
        samplingJobs,
        models,
        trainingJob,
        validationRuns,
        activeValidation,
        validationResults,
        forecastResult,

        // Actions
        refreshComstockStatus,
        startDownload,
        startSampling,
        refreshModels,
        startTraining,
        refreshValidationRuns,
        startValidation,
        startColdStartTest,
        loadValidationResults,
        generateForecast,
        setError,
    };
}
